import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ListView } from '../../../design-system/list-view/ListView'
import { RowActionsMenu } from '../../../design-system/list-view/RowActionsMenu'
import type { Density, SortSpec } from '../../../design-system/list-view/types'
import { usePersistentState } from '../../../lib/usePersistentState'
import { listUsers, deactivateUser, reactivateUser } from '../../../api/userAdmin'
import { ApiError } from '../../../api/problem'
import type { UserView } from '../../../types/userAdmin'
import { USER_COLUMN_ORDER, buildUserColumns } from './columns'
import { InviteUserDialog } from './InviteUserDialog'
import { UserAssignmentDialog } from './UserAssignmentDialog'

const PAGE_LIMIT = 50

/**
 * `/admin/users` — user-admin-entra-federation Slice B (design §2.2, AC-B1..B6): the tenant-wide
 * Users roster, replacing the `/admin/settings` `ComingSoonStub`. Invite/deactivate/reactivate/
 * manage-access all call the real Slice B REST endpoints; a denied caller sees the real error
 * message from a 403 `ApiError`, never a silently-empty list.
 *
 * <p><b>Honest scope note.</b> A still-pending invitation never appears here — Slice B ships no
 * `accept()` HTTP surface (see `InviteUserDialog`'s own note), so only already-accepted users are
 * listed. This screen manages Profile/PermissionSet/Role/Territory *assignments* only — the
 * OLS/FLS/UI-component editors live at `/admin/security` (IB-337 ≡ IB-88 ≡ IB-43,
 * `SecurityAdministrationScreen`), a separate console gated on `security_config:F` rather than the
 * per-user admin capabilities this screen checks.
 */
export function UsersListScreen() {
  const columns = useMemo(() => buildUserColumns(), [])

  const [columnOrder, setColumnOrder] = usePersistentState<string[]>('iw.users.columnOrder', USER_COLUMN_ORDER)
  const [hiddenColumnIdsArr, setHiddenColumnIdsArr] = usePersistentState<string[]>('iw.users.hiddenColumns', [])
  const [pinnedColumnIdsArr, setPinnedColumnIdsArr] = usePersistentState<string[]>('iw.users.pinnedColumns', ['name'])
  const [density, setDensity] = usePersistentState<Density>('iw.users.density', 'compact')

  const hiddenColumnIds = useMemo(() => new Set(hiddenColumnIdsArr), [hiddenColumnIdsArr])
  const pinnedColumnIds = useMemo(() => new Set(pinnedColumnIdsArr), [pinnedColumnIdsArr])

  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'deactivated'>('all')
  const [search, setSearch] = useState('')
  const [sort] = useState<SortSpec[]>([])
  const [selection, setSelection] = useState<Set<string>>(new Set())

  const [users, setUsers] = useState<UserView[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [managing, setManaging] = useState<UserView | null>(null)

  const latestRequestRef = useRef(0)

  const fetchPage = useCallback(async (afterCursor: string | null, replace: boolean) => {
    const requestId = ++latestRequestRef.current
    try {
      if (replace) {
        setLoading(true)
        setError(null)
      } else {
        setLoadingMore(true)
      }
      const page = await listUsers({ cursor: afterCursor, limit: PAGE_LIMIT })
      if (latestRequestRef.current !== requestId) return
      setUsers((prev) => (replace ? page.items : [...prev, ...page.items]))
      setCursor(page.nextCursor)
    } catch (err) {
      if (latestRequestRef.current !== requestId) return
      setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load users. Check your connection and try again.')
    } finally {
      if (latestRequestRef.current === requestId) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }, [])

  useEffect(() => {
    setSelection(new Set())
    void fetchPage(null, true)
  }, [fetchPage])

  function retry() {
    void fetchPage(null, true)
  }

  const visibleUsers = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return users.filter((u) => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false
      if (!needle) return true
      return u.name.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle)
    })
  }, [users, statusFilter, search])

  const quickFilters = useMemo(
    () => [
      { id: 'all', label: 'All' },
      { id: 'active', label: 'Active' },
      { id: 'deactivated', label: 'Deactivated' },
    ],
    [],
  )
  const activeQuickFilterIds = useMemo(() => new Set([statusFilter]), [statusFilter])

  function onQuickFilterToggle(id: string) {
    if (id === 'all' || id === 'active' || id === 'deactivated') setStatusFilter(id)
  }

  function toggleColumnHidden(id: string) {
    setHiddenColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  function moveColumn(id: string, direction: -1 | 1) {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const tmp = next[index]!
      next[index] = next[target]!
      next[target] = tmp
      return next
    })
  }
  function toggleColumnPin(id: string) {
    setPinnedColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }

  async function handleDeactivate(user: UserView) {
    try {
      await deactivateUser(user.id)
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, status: 'deactivated' } : u)))
    } catch (err) {
      setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not deactivate this user.')
    }
  }

  async function handleReactivate(user: UserView) {
    try {
      await reactivateUser(user.id)
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, status: 'active' } : u)))
    } catch (err) {
      setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not reactivate this user.')
    }
  }

  const hasAnyFilterOrSearch = statusFilter !== 'all' || search.trim() !== ''
  const state: 'loading' | 'error' | 'empty-tenant' | 'empty-filtered' | 'ready' = error
    ? 'error'
    : loading
      ? 'loading'
      : visibleUsers.length === 0
        ? hasAnyFilterOrSearch
          ? 'empty-filtered'
          : 'empty-tenant'
        : 'ready'

  return (
    <>
      <ListView<UserView>
        entityIcon="users"
        title="Users"
        countLabel={loading ? undefined : `${visibleUsers.length}${cursor ? '+' : ''}`}
        primaryAction={{ label: 'Invite user', icon: 'user-plus', onClick: () => setInviteOpen(true) }}
        quickFilters={quickFilters}
        activeQuickFilterIds={activeQuickFilterIds}
        onQuickFilterToggle={onQuickFilterToggle}
        search={search}
        onSearchChange={setSearch}
        searchLabel="Search users"
        searchPlaceholder="Search by name or email…"
        filterChildren={<></>}
        activeFilterCount={0}
        activeFilterChips={[]}
        onRemoveFilterChip={() => undefined}
        onClearFilters={() => {
          setStatusFilter('all')
          setSearch('')
        }}
        savedViews={{
          // v64 merge: the product lineage evolved SavedViewsConfig to the named-multiple-views
          // shape. The Users list does not offer saved views (a small admin roster), so this is an
          // inert config matching the current interface — no views, nothing dirty, all no-op handlers.
          views: [],
          isDirty: false,
          onApply: () => undefined,
          onSaveNew: () => undefined,
          onRename: () => undefined,
          onDelete: () => undefined,
          onSetDefault: () => undefined,
          onUnsetDefault: () => undefined,
        }}
        columns={columns}
        columnOrder={columnOrder}
        hiddenColumnIds={hiddenColumnIds}
        pinnedColumnIds={pinnedColumnIds}
        onToggleColumnHidden={toggleColumnHidden}
        onMoveColumn={moveColumn}
        onToggleColumnPin={toggleColumnPin}
        sort={sort}
        onSortChange={() => undefined}
        density={density}
        onDensityChange={setDensity}
        state={state}
        rows={visibleUsers}
        getRowId={(row) => row.id}
        onRowActivate={(row) => setManaging(row)}
        renderRowActions={(row) => (
          <RowActionsMenu
            label={`Actions for ${row.name}`}
            actions={[
              { id: 'manage', label: 'Manage access', icon: 'shield', onSelect: () => setManaging(row) },
              row.status === 'active'
                ? { id: 'deactivate', label: 'Deactivate', icon: 'user-x', onSelect: () => void handleDeactivate(row) }
                : { id: 'reactivate', label: 'Reactivate', icon: 'user-check', onSelect: () => void handleReactivate(row) },
            ]}
          />
        )}
        selection={selection}
        onSelectionChange={setSelection}
        bulkActions={[]}
        errorMessage={error ?? undefined}
        onRetry={retry}
        emptyTenant={{
          icon: 'users',
          title: 'No users yet',
          description: 'Invite the first user to get started.',
          action: (
            <button type="button" className="iw-btn iw-btn--primary" onClick={() => setInviteOpen(true)}>
              Invite user
            </button>
          ),
        }}
        hasMore={Boolean(cursor)}
        onLoadMore={() => void fetchPage(cursor, false)}
        loadingMore={loadingMore}
      />

      {inviteOpen ? (
        <InviteUserDialog onClose={() => setInviteOpen(false)} onInvited={() => void fetchPage(null, true)} />
      ) : null}

      {managing ? (
        <UserAssignmentDialog
          user={managing}
          onClose={() => setManaging(null)}
          onProfileChanged={(profileKey) => {
            setUsers((prev) => prev.map((u) => (u.id === managing.id ? { ...u, profileKey } : u)))
            setManaging((prev) => (prev ? { ...prev, profileKey } : prev))
          }}
        />
      ) : null}
    </>
  )
}
